'use client';

import React, { useState } from "react";
import { Manrope } from "next/font/google";
import {
  CheckCircle,
  Layers,
  Briefcase,
  FileText,
  Users,
  UserCheck,
} from "lucide-react";

const manrope = Manrope({
  subsets: ["latin", "cyrillic"],
  weight: ["400", "600", "700", "800"],
  display: "swap",
});

const benefitItems = [
  { icon: <CheckCircle className="w-7 h-7 text-orange-400" />, title: "Гарантия замены персонала", description: "Бесплатно заменим сотрудника, если он не подойдет" },
  { icon: <Layers className="w-7 h-7 text-orange-400" />, title: "Интеграция с системами", description: "Работаем с 1С, Excel, CRM и другими" },
  { icon: <Briefcase className="w-7 h-7 text-orange-400" />, title: "Экспертиза в 12 отраслях", description: "Опыт подбора и поддержки в разных сегментах бизнеса" },
  { icon: <FileText className="w-7 h-7 text-orange-400" />, title: "Официальное оформление", description: "Работаем в рамках ТК РФ и с полной отчётностью" },
  { icon: <Users className="w-7 h-7 text-orange-400" />, title: "Локальные HR-агенты", description: "Работаем на местах — знаем рынок, понимаем людей" },
  { icon: <UserCheck className="w-7 h-7 text-orange-400" />, title: "Индивидуальный подход", description: "Каждому клиенту предоставляем персонального менеджера" },
];

const services = [
  { title: "Подбор персонала", description: "Закроем вакансию от стандарта до топ-менеджера", link: "/services/recruitment" },
  { title: "Временный персонал", description: "Быстрое усиление штата на нужный период", link: "/services/temporary-staff" },
  { title: "Аутсорсинг функций", description: "Передайте часть задач нам и сократите расходы", link: "/services/outsourcing-functions" },
  { title: "Аутстаффинг", description: "Выведение сотрудников за штат — без потери контроля", link: "/services/outstaffing-functions" },
  { title: "Подбор редких специалистов", description: "Найдём даже самого уникального кандидата", link: "/services/rare-specialists" },
  { title: "Снижение кадровой нагрузки", description: "Автоматизация и делегирование HR-процессов", link: "/services/workload-reduction" },
  { title: "HR-консалтинг", description: "Оптимизируем процессы и поможем избежать рисков", link: "/services/hr-consulting" },
  { title: "Обучение и развитие", description: "Индивидуальные программы обучения и роста", link: "/services/training-development" },
];

const serviceOptions = [
  { label: "Подбор персонала — 20%", value: 20 },
  { label: "Временный персонал — 15%", value: 15 },
  { label: "Аутсорсинг функций — 25%", value: 25 },
  { label: "Аутстаффинг — 15%", value: 15 },
  { label: "Подбор редких специалистов — 20%", value: 20 },
  { label: "Снижение кадровой нагрузки — 15%", value: 15 },
  { label: "HR-консалтинг — 25%", value: 25 },
  { label: "Обучение и развитие — 20%", value: 20 },
];

const testimonials = [
  { author: "Амина Каримова, HR-директор строительной компании", quote: "INTERIM помог нам быстро найти специалистов в строительной отрасли. Процесс был прозрачным, и мы довольны результатом." },
  { author: "Идрис Ильясов, CEO стартапа", quote: "Благодаря аутсорсингу HR-процессов с INTERIM, мы сократили расходы на 30%. Команда отлично справилась с всеми задачами." },
  { author: "Марьям Сулейманова, руководитель отдела кадров", quote: "Агенты INTERIM всегда на связи и готовы помочь. Их профессионализм и скорость работы впечатляют" },
];

const Services: React.FC = () => {
  const [calcResult, setCalcResult] = useState<string | null>(null);
  const [calcStatus, setCalcStatus] = useState<string | null>(null);
  const [consultationStatus, setConsultationStatus] = useState<string | null>(null);
  const [selectedPercentage, setSelectedPercentage] = useState<number>(serviceOptions[0].value);
  const [selectedServiceLabel, setSelectedServiceLabel] = useState<string>(serviceOptions[0].label);

  const [isAgreed, setIsAgreed] = useState<boolean>(false);

  const [consultationFormData, setConsultationFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: services[0].title,
  });

  const handleServiceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOption = serviceOptions.find(opt => opt.value === Number(e.target.value));
    setSelectedPercentage(Number(e.target.value));
    setSelectedServiceLabel(selectedOption ? selectedOption.label : "");
  };

  const handleConsultationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setConsultationFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
    if (e.target.checked) {
      setConsultationStatus(null);
    }
  };

  const handleCalcSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCalcStatus(null);
    setCalcResult(null);

    const formData = new FormData(e.currentTarget);
    const employees = Number(formData.get("employees"));
    const salary = Number(formData.get("salary"));

    if (employees > 0 && salary > 0) {
      const yearlySavings = employees * salary * (selectedPercentage / 100) * 12;

      setCalcResult(`Ваша потенциальная экономия: ${yearlySavings.toLocaleString("ru-RU")} ₽ в год`);
      setCalcStatus("✅ Расчет успешно выполнен.");

    } else {
      setCalcStatus("❌ Пожалуйста, введите корректные положительные числа.");
    }
  };

  const handleConsultationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAgreed) {
      setConsultationStatus("❌ Пожалуйста, примите Политику обработки персональных данных.");
      return;
    }

    setConsultationStatus("Отправка данных...");

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/services-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: {
            ...consultationFormData,
            formName: "Услуги"
          }
        }),
      });

      if (response.ok) {
        setConsultationStatus("✅ Ваша заявка успешно отправлена!");
        setConsultationFormData({
          name: "",
          phone: "",
          email: "",
          service: services[0].title,
        });
        setIsAgreed(false);
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || "Произошла неизвестная ошибка.";
        setConsultationStatus(`❌ Ошибка: ${errorMessage}`);
      }
    } catch (error) {
      setConsultationStatus("❌ Ошибка сети или конфигурации API. Пожалуйста, проверьте консоль.");
      console.error("Ошибка при отправке заявки:", error);
    }
  };

  return (
    <div className={`${manrope.className} bg-[#003087] text-white`}>
      <header className="text-center py-32 px-6 bg-[#003087]">
        <h1 className="text-4xl font-extrabold mb-4">
          Мы решаем кадровые задачи любого уровня сложности
        </h1>
        <p className="text-lg text-gray max-w-3xl mx-auto mb-8">
          INTERIM предлагает полный спектр решений — от подбора персонала до стратегического HR-консалтинга
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="#consultation" className="flex items-center gap-2 bg-[#FFA500] text-black font-semibold px-5 py-3 rounded-xl hover:bg-[#ffb733] transition">
            📞 Запросить консультацию
          </a>
          <a href="#calculate" className="flex items-center gap-2 bg-[#FFA500] text-black font-semibold px-5 py-3 rounded-xl hover:bg-[#ffb733] transition">
            🧮 Рассчитать выгоду
          </a>
        </div>
      </header>

      <section className="bg-[#011b41] px-4 sm:px-6 py-16">
        <div className="max-w-7xl mx-auto grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {services.map((service) => (
            <div key={service.title} className="bg-white text-blue-900 p-6 rounded-xl shadow-md hover:shadow-lg transition flex flex-col h-full">
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-gray-700 mb-4">{service.description}</p>
              <a href={service.link} className="text-orange-500 font-semibold hover:underline mt-auto inline-flex items-center">
                Подробнее
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#1e3faa] py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-center text-3xl sm:text-4xl font-bold text-[#FFA500] mb-12">Почему INTERIM?</h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 text-left">
            {benefitItems.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="text-[#FFA500] flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="text-[#bcd2f0] text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="calculate" className="py-16 px-6 bg-[#1e3faa] scroll-mt-32">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-center text-[#FFA500] mb-6">
            Сколько вы можете сэкономить с INTERIM?
          </h2>
          <div className="max-w-md mx-auto p-6 bg-white/10 backdrop-blur-md rounded-xl shadow-lg text-gray-100">
            <h3 className="text-2xl font-semibold mb-4">Рассчитайте вашу выгоду</h3>
            <p className="text-sm text-white mb-4">
              Введите данные, чтобы оценить приблизительную экономию при сотрудничестве с INTERIM.
            </p>
            <form onSubmit={handleCalcSubmit} className="space-y-4">
              <div>
                <label htmlFor="employees" className="block text-sm font-medium text-white">Количество сотрудников</label>
                <input type="number" id="employees" name="employees" min={1} className="mt-1 block w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900" placeholder="Например, 10" required />
              </div>
              <div>
                <label htmlFor="salary" className="block text-sm font-medium text-white">Средняя зарплата, ₽</label>
                <input type="number" id="salary" name="salary" min={10000} className="mt-1 block w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900" placeholder="Например, 60000" required />
              </div>
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-white">Тип услуги</label>
                <select id="serviceType" name="serviceType" className="mt-1 block w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900" value={selectedPercentage} onChange={handleServiceChange} required>
                  {serviceOptions.map((option) => (
                    <option key={option.label} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">Рассчитать</button>
            </form>
            {calcResult && (
              <div className="mt-3 p-3 bg-green-100 text-green-800 rounded-xl">
                {calcResult}
              </div>
            )}
            {calcStatus && (
              <div className="mt-3 text-sm text-center">
                {calcStatus}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#1e3faa]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-center text-[#FFA500] mb-10">Отзывы клиентов</h2>
          <div className="flex space-x-6 overflow-x-auto snap-x pb-4 -mx-6 px-6">
            {testimonials.map((test) => (
              <div key={test.author} className="snap-start flex-shrink-0 w-80 bg-white/10 backdrop-blur-md p-6 rounded-xl shadow-lg">
                <p className="text-gray-200 mb-4 italic">{test.quote}</p>
                <p className="text-[#FFA500] font-semibold">{test.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="consultation" className="py-16 px-6 bg-[#1e3faa] scroll-mt-32">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-semibold text-center text-[#FFA500] mb-10">Свяжитесь с нами</h2>
          <form onSubmit={handleConsultationSubmit} className="max-w-xl mx-auto space-y-6 bg-white/10 backdrop-blur-md p-8 rounded-xl shadow-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-1">Имя</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={consultationFormData.name}
                  onChange={handleConsultationChange}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900"
                  required />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-200 mb-1">Телефон</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={consultationFormData.phone}
                  onChange={handleConsultationChange}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900"
                  required />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-1">E-mail</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={consultationFormData.email}
                  onChange={handleConsultationChange}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900"
                  required />
              </div>
              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-200 mb-1">Выберите услугу</label>
                <select
                  id="service"
                  name="service"
                  value={consultationFormData.service}
                  onChange={handleConsultationChange}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-gray-900"
                  required >
                  {services.map((service) => (
                    <option key={service.title} value={service.title}>{service.title}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                id="privacyAgreement"
                checked={isAgreed}
                onChange={handleAgreementChange}
                className="mt-1 mr-2 w-4 h-4 text-yellow-500 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
              />
              <label htmlFor="privacyAgreement" className="text-xs font-light text-gray-200 cursor-pointer">
                Нажимая "Получить консультацию", вы соглашаетесь с&nbsp;
                <a href="/privacy" className="text-yellow-500 hover:underline font-normal" target="_blank" rel="noopener noreferrer">
                  Политикой обработки персональных данных
                </a>
                .
              </label>
            </div>

            <div>
              <button
                type="submit"
                className="w-full bg-yellow-500 text-blue-900 font-semibold py-3 rounded-xl hover:bg-yellow-600 transition"
              >
                Получить консультацию
              </button>
            </div>
            {consultationStatus && (
              <div className={`mt-3 text-sm text-center ${consultationStatus.startsWith('❌') ? 'text-red-400' : 'text-green-400'}`}>
                {consultationStatus}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Services;